<template>
  <div class="dd-feed" :class="{ 'dd-feed--single': singleColumn }">
    <div
      v-if="showLoginTile && !authenticated"
      :class="['dd-wall-card', 'dd-login-post', { 'dd-feed__item': singleColumn }]"
    >
      <h3 class="dd-post__title">{{ $t('wall.home.login_tile_title') }}</h3>
      <p class="dd-post__text">{{ $t('wall.home.login_tile_text') }}</p>
      <div class="dd-post__cta" style="display:flex;justify-content:flex-end">
        <SigninWithGoogle/>
      </div>
    </div>
    <template v-if="singleColumn">
      <PostCard v-for="p in posts" :key="p.id" :post="p" class="dd-feed__item"/>
    </template>
    <template v-else>
      <div class="dd-col">
        <PostCard v-for="p in leftPosts" :key="p.id" :post="p"/>
      </div>
      <div class="dd-col">
        <PostCard v-for="p in rightPosts" :key="p.id" :post="p"/>
      </div>
    </template>
    <button v-if="showToTop" class="dd-to-top" @click="scrollTop"><img :src="arrowUp" alt="up"/></button>
    <div ref="infiniteAnchor" class="dd-infinite-anchor"></div>
    <div v-if="loadingMore" class="dd-spinner-fixed">
      <Spinner :size="40" :thickness="4"/>
    </div>
  </div>
</template>

<script>
import PostCard from '@/new/components/wall/PostCard.vue';
import Spinner from '@/new/components/general/Spinner.vue';
import SigninWithGoogle from '@/new/components/general/SigninWithGoogle.vue';
import {logWarn} from '@/helpers';

export default {
  name: 'PostFeed',
  components: {PostCard, Spinner, SigninWithGoogle},
  data() {
    return {
      showToTop: false,
      singleColumn: (typeof window !== 'undefined' && typeof window.matchMedia === 'function')
        ? window.matchMedia('(max-width: 680px)').matches
        : false,
    };
  },
  props: {
    showLoginTile: {type: Boolean, default: false},
  },
  computed: {
    arrowUp() {
      return require('@/assets/img/arrow-up.svg');
    },
    posts() {
      return this.$store.getters.posts || [];
    },
    loadingMore() {
      return this.$store.getters.postsPagination?.loading;
    },
    nextPageToken() {
      return this.$store.getters.postsPagination?.nextPageToken || null;
    },
    authenticated() {
      return this.$store.getters.authenticated;
    },
    leftPosts() {
      const out = [];
      const src = this.posts || [];
      for (let i = 0; i < src.length; i += 2) out.push(src[i]);
      return out;
    },
    rightPosts() {
      const out = [];
      const src = this.posts || [];
      for (let i = 1; i < src.length; i += 2) out.push(src[i]);
      return out;
    },
  },
  mounted() {
    this.createObserver();
    this.setupViewportWatcher();
    this._onScroll = () => {
      this.showToTop = (window.scrollY || window.pageYOffset || 0) > 60;
    };
    window.addEventListener('scroll', this._onScroll, {passive: true});
  },
  watch: {
    nextPageToken(val) {
      // Stop observing when there is nothing more to load
      if (!val && this.observer && this.$refs.infiniteAnchor) {
        try {
          this.observer.unobserve(this.$refs.infiniteAnchor);
        } catch (e) {
          logWarn('Failed to unobserve infinite scroll anchor', e);
        }
      }
    }
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    this.teardownViewportWatcher();
  },
  methods: {
    createObserver() {
      if (!('IntersectionObserver' in window)) return;
      if (!this.nextPageToken) return; // nothing to load
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            if (this._loadLock) return;
            this._loadLock = true;
            if (this.nextPageToken) {
              this.$store.dispatch('loadMorePosts');
            }
            clearTimeout(this._loadTimer);
            this._loadTimer = setTimeout(() => {
              this._loadLock = false;
            }, 600);
          }
        });
      }, {root: null, rootMargin: '0px', threshold: 0.1});
      if (this.$refs.infiniteAnchor) this.observer.observe(this.$refs.infiniteAnchor);
    },
    setupViewportWatcher() {
      if (typeof window === 'undefined' || !window.matchMedia) return;
      this._mq = window.matchMedia('(max-width: 680px)');
      this.singleColumn = this._mq.matches;
      this._mqListener = (event) => {
        this.singleColumn = !!event.matches;
      };
      if (typeof this._mq.addEventListener === 'function') {
        this._mq.addEventListener('change', this._mqListener);
      } else if (typeof this._mq.addListener === 'function') {
        this._mq.addListener(this._mqListener);
      }
    },
    teardownViewportWatcher() {
      if (!this._mq || !this._mqListener) return;
      if (typeof this._mq.removeEventListener === 'function') {
        this._mq.removeEventListener('change', this._mqListener);
      } else if (typeof this._mq.removeListener === 'function') {
        this._mq.removeListener(this._mqListener);
      }
      this._mq = null;
      this._mqListener = null;
    },
    scrollTop() {
      try {
        window.scrollTo({top: 0, behavior: 'smooth'});
      } catch (e) {
        logWarn('Smooth scroll is not fully supported, falling back to instant scroll', e);
        window.scrollTo(0, 0);
      }
    },
  }
}
</script>

<style scoped>
.dd-feed {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.dd-feed--single {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.dd-feed__item {
  width: 100%;
}

.dd-feed--single .dd-feed__item + .dd-feed__item {
  margin-top: 16px;
}

.dd-login-post {
  grid-column: 1 / -1;
  height: auto;
}

.dd-col > * {
  margin-bottom: 16px;
}

.dd-infinite-anchor {
  grid-column: 1 / -1;
  height: 1px;
}

.dd-feed--single .dd-infinite-anchor {
  align-self: stretch;
}

.dd-spinner-fixed {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 24px;
  z-index: 1000;
}

.dd-to-top {
  position: fixed;
  right: 20px;
  bottom: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--dd-accent);
  color: var(--dd-accent-contrast);
  border: none;
  box-shadow: var(--dd-shadow);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.dd-to-top img {
  filter: invert(1);
  width: 20px;
  height: 20px;
}
</style>
